import { apiHost, emitTelemetry, telemetryEnabled, telemetryId, type XmaxTelemetryEventName, type XmaxTelemetryParams } from './collector'
import { diagnosticError } from './startup'

function endpointTemplate(path: string): string {
  const clean = path.split('?')[0]
  if (['/session', '/cos/sts', '/cos/image/check', '/offline-task', '/offline-task/page', '/offline-task/batch-query'].includes(clean)) return clean
  if (/^\/session\/[^/]+\/heartbeat$/.test(clean)) return '/session/:id/heartbeat'
  if (/^\/session\/[^/]+$/.test(clean)) return '/session/:id'
  if (/^\/offline-task\/[^/]+$/.test(clean)) return '/offline-task/:id'
  return '/other'
}

export class HttpTrace {
  private readonly startedAt = performance.now()
  private headersAt?: number
  private bodyAt?: number
  private endedAt?: number
  private finished = false
  private outcome = 'success'
  private stage = 'prepare'
  private requestRecorded = false
  private readonly params: XmaxTelemetryParams

  constructor(
    method: string,
    path: string,
    baseUrl: string,
    timeoutMs: number,
    context?: XmaxTelemetryParams,
    private readonly recorder?: (name: XmaxTelemetryEventName, params: XmaxTelemetryParams) => void,
  ) {
    this.params = { ...context, request_id: telemetryId(), method, endpoint: endpointTemplate(path), api_host: apiHost(baseUrl), timeout_ms: timeoutMs, attempt: 1 }
  }
  private emit(name: XmaxTelemetryEventName, params: XmaxTelemetryParams): void {
    if (this.recorder) this.recorder(name, params)
    else emitTelemetry(name, params)
  }
  private eventNames(): { request: XmaxTelemetryEventName; response: XmaxTelemetryEventName } | undefined {
    if (this.params.method === 'POST' && this.params.endpoint === '/session') return { request: 'sdk_api_session_request', response: 'sdk_api_session_response' }
    if (this.params.method === 'GET' && this.params.endpoint === '/cos/sts') return { request: 'sdk_asset_sts_request', response: 'sdk_asset_sts_response' }
    return undefined
  }
  sending(): void {
    this.stage = 'fetch'
    const names = this.eventNames()
    if (names && !this.requestRecorded) {
      this.requestRecorded = true
      this.emit(names.request, { ...this.params })
    }
  }
  headers(status: number): void {
    this.headersAt = performance.now()
    this.params.http_status = status
    this.stage = 'read_body'
  }
  body(): void { this.bodyAt = performance.now(); this.stage = 'parse' }
  businessCode(code: unknown): void {
    if (typeof code === 'string' || typeof code === 'number') this.params.business_code = String(code)
  }
  error(error: unknown, outcome: 'error' | 'timeout' | 'cancelled', reason?: string): void {
    this.endedAt = performance.now()
    this.outcome = outcome
    Object.assign(this.params, diagnosticError(error), { failure_stage: this.stage, failure_reason: reason })
  }
  finish(): void {
    if (this.finished) return
    this.finished = true
    const now = this.endedAt ?? performance.now()
    const duration = Math.round(now - this.startedAt)
    const payload = {
      ...this.params, outcome: this.outcome, duration_ms: duration, sample_rate: 1,
      ok: this.outcome === 'success',
      headers_wait_ms: this.headersAt === undefined ? undefined : Math.round(this.headersAt - this.startedAt),
      body_read_ms: this.headersAt === undefined || this.bodyAt === undefined ? undefined : Math.round(this.bodyAt - this.headersAt),
      parse_ms: this.bodyAt === undefined ? undefined : Math.round(now - this.bodyAt),
    }
    const names = this.eventNames()
    this.emit(names?.response ?? 'sdk_api_request', payload)
  }
}

export function beginHttpTrace(
  enabled: boolean | undefined,
  method: string,
  path: string,
  baseUrl: string,
  timeoutMs: number,
  context?: () => XmaxTelemetryParams | undefined,
  recorder?: (name: XmaxTelemetryEventName, params: XmaxTelemetryParams) => void,
): HttpTrace | undefined {
  if (enabled === false || !telemetryEnabled()) return undefined
  try { return new HttpTrace(method, path, baseUrl, timeoutMs, context?.(), recorder) } catch { return undefined }
}
